import React, { useState, useCallback } from 'react';
import '@/components/ui/SectionDividerWithLines/SectionDividerWithLines.css';
import './DirectMessageBubble.css';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import type { MessengerMessage } from '@/api/client';
import { useTeleportOptional } from '@/contexts/TeleportContext';
import { MessengerMessageRow } from '@/components/Messenger/shared/messages/MessengerMessageRow';
import { MessageMeta } from '@/components/Messenger/shared/messages/MessageMeta';
import { DmMessageBubble } from '@/components/Messenger/shared/messages/DmMessageBubble';
import { MessengerDmFileAttachment } from '@/components/Messenger/shared/attachments/MessengerDmFileAttachment';
import { MessengerInlineImage } from '@/components/Messenger/shared/attachments/MessengerInlineImage';
import { isMessengerImageMime } from '@/components/Messenger/shared/attachments/messengerImageMime';
import { messengerForwardedSenderName } from '@/components/Messenger/shared/attachments/messengerForwardedSenderName';
import {
  ContextAttachmentChips,
  type MessengerContextAttachment,
} from '@/components/Messenger/shared/messages/ContextAttachmentChips';
import { ReplyQuote } from '@/components/Messenger/shared/messages/ReplyQuote';
import { ForwardedHeader } from '@/components/Messenger/shared/messages/ForwardedHeader';
import { MessageContextMenu } from '@/components/Messenger/shared/messages/MessageContextMenu';
import { MessageLikeStrip } from '@/components/Messenger/shared/messages/MessageLikeStrip';
import { MessageTextWithMeta } from '@/components/Messenger/shared/messages/MessageTextWithMeta';
import { MessageRichWithMeta } from '@/components/Messenger/shared/messages/MessageRichWithMeta';
import { MessengerPeerBubbleInlineTextAndTime } from '@/components/Messenger/shared/messages/MessengerPeerBubbleInlineTextAndTime';
import { BubbleShell } from '@/components/Messenger/shared/messages/BubbleShell';
import { resolveBubbleMode } from '@/components/Messenger/shared/messages/bubbleMode';
import { isMessengerOptimisticMessageId } from '@/constants/messengerOptimistic';
import { MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY } from '@/constants/messengerSearchHighlight';
import { SEARCH_HIGHLIGHT_CLASS_MESSENGER } from '@/utils/highlightSearchTerms';
import { linkifyPlainText } from '@/utils/linkifyPlainText';
import { getSelectedPlainTextWithinContainer } from '@/components/Messenger/shared/messages/messageContextCopy';

/** memo: сравниваем поля сообщения, включая лайки (не только ссылку на объект). */
function dmBubbleMessageEqualForMemo(a: MessengerMessage, b: MessengerMessage): boolean {
  if (a === b) return true;
  if (a.id !== b.id) return false;
  return (
    (a.message_kind ?? 'user') === (b.message_kind ?? 'user') &&
    a.body === b.body &&
    a.edited_at === b.edited_at &&
    a.deleted_at === b.deleted_at &&
    a.read_at === b.read_at &&
    a.delivered_at === b.delivered_at &&
    a.file_url === b.file_url &&
    a.file_name === b.file_name &&
    a.mime_type === b.mime_type &&
    a.image_width === b.image_width &&
    a.image_height === b.image_height &&
    JSON.stringify(a.context_attachments ?? null) === JSON.stringify(b.context_attachments ?? null) &&
    a.reply_to_id === b.reply_to_id &&
    JSON.stringify(a.reply_to ?? null) === JSON.stringify(b.reply_to ?? null) &&
    a.forwarded_from_message_id === b.forwarded_from_message_id &&
    a.forwarded_from_label === b.forwarded_from_label &&
    a.forwarded_from_sender_name === b.forwarded_from_sender_name &&
    a.sender_display_name === b.sender_display_name &&
    (a.like_count ?? 0) === (b.like_count ?? 0) &&
    Boolean(a.liked_by_me) === Boolean(b.liked_by_me) &&
    Boolean(a.is_pinned) === Boolean(b.is_pinned) &&
    JSON.stringify(a.like_likers_preview ?? []) === JSON.stringify(b.like_likers_preview ?? []) &&
    a._client === b._client
  );
}

function normalizeContextAttachments(
  raw:
    | Array<{ type?: string; id?: string; name?: string; date?: string; label?: string }>
    | undefined
): MessengerContextAttachment[] {
  if (!Array.isArray(raw)) return [];
  const out: MessengerContextAttachment[] = [];
  for (const a of raw) {
    if (a?.type === 'project' && a.id) {
      out.push({ type: 'project', id: a.id, name: a.name });
    } else if (a?.type === 'session' && a.id && a.date) {
      out.push({ type: 'session', id: a.id, date: a.date, label: a.label });
    }
  }
  return out;
}

interface DirectMessageBubbleProps {
  msg: MessengerMessage;
  anchorId: string;
  isOwn: boolean;
  showUnreadDivider: boolean;
  currentUserId: string | null;
  onRetry?: (optimisticId: string) => void;
  onReply?: (msg: MessengerMessage) => void;
  onForward?: (msg: MessengerMessage) => void;
  onDelete?: (msg: MessengerMessage) => void;
  onStartEdit?: (msg: MessengerMessage) => void;
  onPin?: (msg: MessengerMessage) => void;
  onUnpin?: (msg: MessengerMessage) => void;
  onJumpToMessage?: (messageId: string) => void;
  selectionMode?: boolean;
  onToggleSelect?: (msg: MessengerMessage) => void;
  onToggleMessageLike?: (msg: MessengerMessage) => void;
  showIncomingAuthorName?: boolean | string | null;
  relaxDeleteAgeLimit?: boolean;
  animateEnter?: boolean;
  /** Открытие пункта «Просмотрено» в меню только для групп */
  threadVariant?: 'direct' | 'group';
  /** Слова поиска сайдбара — подсветка в теле и цитате */
  sidebarSearchHighlightWords?: readonly string[];
}

function DirectMessageBubbleInner({
  msg,
  anchorId,
  isOwn,
  showUnreadDivider,
  currentUserId,
  onRetry,
  onReply,
  onForward,
  onDelete,
  onStartEdit,
  onPin,
  onUnpin,
  onJumpToMessage,
  selectionMode = false,
  onToggleSelect,
  onToggleMessageLike,
  showIncomingAuthorName = false,
  relaxDeleteAgeLimit = false,
  animateEnter = false,
  threadVariant = 'direct',
  sidebarSearchHighlightWords = MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY,
}: DirectMessageBubbleProps) {
  const teleport = useTeleportOptional();
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [contextMenuSelectedText, setContextMenuSelectedText] = useState('');
  /** Ширина слота изображения (px) — передаётся в BubbleShell, чтобы бабл не расширялся текстом */
  const [imageSlotWidth, setImageSlotWidth] = useState<number | null>(null);



  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (selectionMode) return;
      e.preventDefault();
      const container = e.currentTarget as Element;
      setContextMenuSelectedText(getSelectedPlainTextWithinContainer(container));
      setContextMenuPos({ x: e.clientX, y: e.clientY });
    },
    [selectionMode],
  );

  if (msg.message_kind === 'system') {
    const systemReplyPreview = msg.reply_to ? (
      <div className="directChatView__serviceMsgPreview">
        <ReplyQuote
          replyTo={msg.reply_to}
          isOwn={false}
          searchHighlightWords={sidebarSearchHighlightWords}
          onJumpToMessage={onJumpToMessage}
          variant="card"
        />
      </div>
    ) : null;

    return (
      <div className="directChatView__msgSlot directChatView__msgSlot_system" data-dm-msg={anchorId}>
        {showUnreadDivider && (
          <div className="section-divider-with-lines">
            <span className="section-divider-with-lines__label">Непрочитанные сообщения</span>
          </div>
        )}
        <div className="directChatView__serviceMsg" role="status">
          <span className="directChatView__serviceMsgText">{msg.body}</span>
          {systemReplyPreview}
        </div>
      </div>
    );
  }

  const isDeleted = Boolean(msg.deleted_at);

  const ctxAttachments = msg.context_attachments as
    | Array<{ type?: string; id?: string; name?: string; date?: string; label?: string }>
    | undefined;
  const ctxNorm = normalizeContextAttachments(ctxAttachments);

  const showImage = !isDeleted && Boolean(msg.file_url && isMessengerImageMime(msg.mime_type));
  const showFileAttachment =
    !isDeleted && Boolean(msg.file_url && !isMessengerImageMime(msg.mime_type));

  const hasBodyText = !isDeleted && Boolean(msg.body?.trim().length);

  const incomingAuthorLabel = !isOwn
    ? typeof showIncomingAuthorName === 'string'
      ? showIncomingAuthorName.trim() || null
      : showIncomingAuthorName
        ? (msg.sender_display_name ?? '').trim() || 'Участник'
        : null
    : null;

  const hasCtxAttachments = ctxNorm.length > 0;
  const hasAttachmentCards = showFileAttachment || hasCtxAttachments;
  const forwardedSenderName = messengerForwardedSenderName(msg);

  /** «Переслано от» в шапке карточки — только если вложение без текста сообщения. */
  const showForwardInAttachmentCard =
    !isDeleted && hasAttachmentCards && !hasBodyText && Boolean(forwardedSenderName);

  const forwardOnContext = showForwardInAttachmentCard && hasCtxAttachments;
  const forwardOnFile = showForwardInAttachmentCard && showFileAttachment && !hasCtxAttachments;

  /** Ответ в шапке карточки — только если вложение без текста сообщения. */
  const showReplyInAttachmentCard =
    !isDeleted && hasAttachmentCards && !hasBodyText && Boolean(msg.reply_to);

  const replyOnContext = showReplyInAttachmentCard && hasCtxAttachments;
  const replyOnFile = showReplyInAttachmentCard && showFileAttachment && !hasCtxAttachments;

  const attachmentHeaderReply =
    showReplyInAttachmentCard && msg.reply_to ? (
      <ReplyQuote
        replyTo={msg.reply_to}
        isOwn={isOwn}
        searchHighlightWords={sidebarSearchHighlightWords}
        onJumpToMessage={onJumpToMessage}
        variant="attachmentHeader"
      />
    ) : null;

  /** Имя в шапке карточки — группа, без текста, на первом вложении. */
  const attachmentCardPeerAuthor =
    !isDeleted && hasAttachmentCards && !hasBodyText && !isOwn ? incomingAuthorLabel : null;

  const authorOnContext = attachmentCardPeerAuthor && hasCtxAttachments;
  const authorOnFile = attachmentCardPeerAuthor && showFileAttachment && !hasCtxAttachments;

  const ctxHeaderInTextCol =
    isOwn || hasBodyText || authorOnContext ? null : incomingAuthorLabel;

  const fileHeaderInTextCol =
    isOwn || hasBodyText || authorOnFile || forwardOnFile ? null : incomingAuthorLabel;

  // Удалённое сообщение отображается как текстовый пузырь («Сообщение удалено»)
  const effectiveHasText = hasBodyText || isDeleted;

  const mode = resolveBubbleMode({
    hasText: effectiveHasText,
    hasImage: showImage,
    hasAttachment: showFileAttachment || ctxNorm.length > 0,
  });

  const timeLabel = format(new Date(msg.created_at), 'HH:mm', { locale: ru });
  const isFailed = isOwn && msg._client?.status === 'failed';
  const deliveryPending =
    isOwn &&
    isMessengerOptimisticMessageId(msg.id) &&
    msg._client?.status !== 'failed';

  const likeCount = msg.like_count ?? 0;
  const likedByMe = Boolean(msg.liked_by_me);
  const showLikeStrip = !isDeleted && (likeCount > 0 || likedByMe);
  const likeStripInteractive = Boolean(
    currentUserId && onToggleMessageLike && !isMessengerOptimisticMessageId(msg.id),
  );

  const likeStripNode = showLikeStrip ? (
    <MessageLikeStrip
      count={likeCount}
      likedByMe={likedByMe}
      variant={isOwn ? 'own' : 'peer'}
      likersPreview={msg.like_likers_preview}
      interactive={likeStripInteractive}
      onToggle={
        likeStripInteractive && onToggleMessageLike
          ? () => onToggleMessageLike(msg)
          : undefined
      }
    />
  ) : null;

  // Для imageOnly лайк живёт ВНЕ пузыря (ниже картинки)
  const likeOutsideImageOnly = showLikeStrip && mode === 'imageOnlyBubble';
  // Для textBubble лайк идёт в footer-строку BubbleShell
  const likeInFooterRow = showLikeStrip && mode === 'textBubble';
  // Для attachmentOnly лайк внутри пузыря под карточкой
  const likeInsideAttachment = showLikeStrip && mode === 'attachmentOnlyBubble';

  const replyQuoteSlot =
    !isDeleted && msg.reply_to && !showReplyInAttachmentCard ? (
      <ReplyQuote
        replyTo={msg.reply_to}
        isOwn={isOwn}
        searchHighlightWords={sidebarSearchHighlightWords}
        onJumpToMessage={onJumpToMessage}
        variant={mode === 'attachmentOnlyBubble' ? 'card' : 'default'}
      />
    ) : null;

  const forwardedHeaderSlot =
    !isDeleted && forwardedSenderName && !showForwardInAttachmentCard ? (
      <ForwardedHeader senderName={forwardedSenderName} isOwn={isOwn} />
    ) : null;

  const fileAttachmentSlot =
    showFileAttachment && msg.file_url ? (
      <div className="chatAssistant__dmAttachmentBlock">
        <MessengerDmFileAttachment
          href={msg.file_url}
          header={fileHeaderInTextCol}
          peerAuthorName={authorOnFile ? attachmentCardPeerAuthor : null}
          forwardedFromSenderName={forwardOnFile ? forwardedSenderName : null}
          replyQuote={replyOnFile ? attachmentHeaderReply : null}
          fileName={msg.file_name}
          fileSizeBytes={msg.file_size}
          isOwn={isOwn}
          attachmentPhase={isOwn ? msg._client?.attachmentPhase : undefined}
        />
      </div>
    ) : null;

  const ctxRow =
    ctxNorm.length > 0 ? (
      <ContextAttachmentChips
        attachments={ctxNorm}
        teleport={teleport}
        header={ctxHeaderInTextCol}
        peerAuthorName={authorOnContext ? attachmentCardPeerAuthor : null}
        forwardedFromSenderName={forwardOnContext ? forwardedSenderName : null}
        replyQuote={replyOnContext ? attachmentHeaderReply : null}
        isOwn={isOwn}
      />
    ) : null;

  /** Единый порядок слотов: forward → reply → context → file → image → text (+ meta/footer по режиму). */
  // ── Рендер по режимам ──────────────────────────────────────────────────────

  /** Блок изображения — используется в imageOnlyBubble и в textBubble (как pre-content) */
  const buildImageBlock = (withCornerMeta: boolean) => {
    if (!showImage || !msg.file_url) return null;
    return (
      <div className="chatAssistant__userMsgImageBlock">
        <MessengerInlineImage
          src={msg.file_url}
          alt={msg.file_name}
          intrinsicWidth={msg.image_width ?? undefined}
          intrinsicHeight={msg.image_height ?? undefined}
          onSlotResize={(w) => setImageSlotWidth(w)}
        />

        {withCornerMeta ? (
          <MessageMeta
            variant="corner"
            timeLabel={timeLabel}
            showReadReceipts={isOwn}
            readAt={isOwn ? msg.read_at : undefined}
            deliveredAt={isOwn ? msg.delivered_at : undefined}
            isOwn={isOwn}
            deliveryPending={isOwn ? deliveryPending : undefined}
          />
        ) : null}
        {isOwn && msg._client?.attachmentPhase ? (
          <div
            className="chatAssistant__attachmentUploadOverlay"
            aria-busy="true"
            aria-label={
              msg._client.attachmentPhase === 'uploading' ? 'Загрузка файла' : 'Отправка сообщения'
            }
          >
            <span className="chatAssistant__attachmentUploadSpinner" />
          </div>
        ) : null}
      </div>
    );
  };

  /** Текстовый слот для textBubble */
  const buildTextNode = (hasFooter: boolean): React.ReactNode => {
    const bodyClassName = isOwn ? 'chatAssistant__userMessageText' : 'chatAssistant__dmPeerBody';
    const hl = sidebarSearchHighlightWords;

    if (isDeleted) {
      const deletedLabel = 'Сообщение удалено';
      if (hasFooter) {
        return <em className={`dmBubble__deletedText ${bodyClassName}`}>{deletedLabel}</em>;
      }
      return (
        <MessageTextWithMeta
          text={deletedLabel}
          bodyClassName={`${bodyClassName} dmBubble__deletedText`}
          meta={
            <MessageMeta
              timeLabel={timeLabel}
              showReadReceipts={isOwn}
              readAt={isOwn ? msg.read_at : undefined}
              deliveredAt={isOwn ? msg.delivered_at : undefined}
              isOwn={isOwn}
              deliveryPending={isOwn ? deliveryPending : undefined}
              edited={false}
              inline
            />
          }
        />
      );
    }

    if (!msg.body?.length) return null;

    const bodyContent = linkifyPlainText(msg.body, {
      highlightWords: hl,
      highlightClassName: SEARCH_HIGHLIGHT_CLASS_MESSENGER,
    });

    const metaInline = (
      <MessageMeta
        timeLabel={timeLabel}
        showReadReceipts={isOwn}
        readAt={isOwn ? msg.read_at : undefined}
        deliveredAt={isOwn ? msg.delivered_at : undefined}
        isOwn={isOwn}
        deliveryPending={isOwn ? deliveryPending : undefined}
        edited={Boolean(msg.edited_at)}
        inline
      />
    );

    if (hasFooter) {
      return <div className={bodyClassName}>{bodyContent}</div>;
    }

    if (hl.length > 0) {
      return (
        <MessageTextWithMeta
          text={msg.body}
          searchHighlightWords={hl}
          bodyClassName={bodyClassName}
          meta={metaInline}
        />
      );
    }

    if (!isOwn) {
      return (
        <MessengerPeerBubbleInlineTextAndTime
          bodyClassName={bodyClassName}
          timeLabel={timeLabel}
          body={bodyContent}
        />
      );
    }

    return (
      <MessageRichWithMeta
        bodyClassName={bodyClassName}
        body={bodyContent}
        meta={metaInline}
      />
    );
  };

  /** Мета для footer-строки BubbleShell */
  const buildFooterMeta = () => (
    <MessageMeta
      timeLabel={timeLabel}
      showReadReceipts={isOwn}
      readAt={isOwn ? msg.read_at : undefined}
      deliveredAt={isOwn ? msg.delivered_at : undefined}
      isOwn={isOwn}
      deliveryPending={isOwn ? deliveryPending : undefined}
      edited={Boolean(msg.edited_at)}
    />
  );

  const attachmentOnlyStack = (
    <div
      className={[
        'chatAssistant__dmAttachmentOnlyStack',
        isOwn ? 'chatAssistant__dmAttachmentOnlyStack_own' : 'chatAssistant__dmAttachmentOnlyStack_peer',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {forwardedHeaderSlot}
      {replyQuoteSlot}
      <div className="chatAssistant__dmAttachmentMetaHost">
        {ctxRow}
        {fileAttachmentSlot}
        <MessageMeta
          variant="corner"
          timeLabel={timeLabel}
          showReadReceipts={isOwn}
          readAt={isOwn ? msg.read_at : undefined}
          deliveredAt={isOwn ? msg.delivered_at : undefined}
          isOwn={isOwn}
          deliveryPending={isOwn ? deliveryPending : undefined}
          edited={Boolean(msg.edited_at)}
        />
      </div>
      {likeInsideAttachment ? likeStripNode : null}
    </div>
  );

  /** Единая оболочка DM-пузыря (исходящий / входящий) — см. `DmMessageBubble`. */
  const renderDmBubble = () => {
    const variant = isOwn ? 'own' : 'peer';

    // Карточка проекта без текста — тоже в пузыре (концепт project-to-chat 2.2): время строкой под ней, не поверх отметок
    if (mode === 'attachmentOnlyBubble' && !showFileAttachment && ctxNorm.some((a) => a.type === 'project')) {
      return (
        <DmMessageBubble variant={variant} peerSenderName={null}>
          <BubbleShell
            content={
              <>
                {forwardedHeaderSlot}
                {replyQuoteSlot}
                {ctxRow}
              </>
            }
            footer={likeInsideAttachment ? likeStripNode : undefined}
            meta={buildFooterMeta()}
          />
        </DmMessageBubble>
      );
    }

    if (mode === 'imageOnlyBubble') {
      const imageSlotStyle: React.CSSProperties | undefined =
        showImage && imageSlotWidth != null && imageSlotWidth > 0
          ? { width: `${imageSlotWidth}px`, maxWidth: '100%' }
          : undefined;
      const hasTopSlots = Boolean(forwardedHeaderSlot || replyQuoteSlot);
      return (
        <DmMessageBubble
          variant={variant}
          peerSenderName={isOwn ? null : incomingAuthorLabel}
          noPadding={!isOwn}
        >
          <div style={imageSlotStyle}>
            {hasTopSlots ? (
              <div className="chatAssistant__imageOnlyTopSlots">
                {forwardedHeaderSlot}
                {replyQuoteSlot}
              </div>
            ) : null}
            {buildImageBlock(true)}
          </div>
        </DmMessageBubble>
      );
    }

    if (mode === 'attachmentOnlyBubble') {
      return (
        <DmMessageBubble
          variant={variant}
          surface="attachmentBare"
          peerSenderName={null}
        >
          {attachmentOnlyStack}
        </DmMessageBubble>
      );
    }

    return (
      <DmMessageBubble variant={variant} peerSenderName={isOwn ? null : incomingAuthorLabel}>
        <BubbleShell
          content={
            <>
              {forwardedHeaderSlot}
              {replyQuoteSlot}
              {ctxRow}
              {fileAttachmentSlot}
              {buildImageBlock(false)}
              {buildTextNode(likeInFooterRow)}
            </>
          }
          footer={likeInFooterRow ? likeStripNode : undefined}
          meta={likeInFooterRow ? buildFooterMeta() : undefined}
          slotWidth={showImage ? imageSlotWidth : null}
        />
      </DmMessageBubble>
    );
  };

  // ── Обёртка outside-like для imageOnly ───────────────────────────────────

  const outsideLikeWrapClass = [
    'chatAssistant__dmOutsideLikeWrap',
    likeOutsideImageOnly
      ? isOwn
        ? 'chatAssistant__dmOutsideLikeWrap_own'
        : 'chatAssistant__dmOutsideLikeWrap_peer'
      : 'chatAssistant__dmOutsideLikeWrap_noOutside',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="directChatView__msgSlot"
      data-dm-msg={anchorId}
      onContextMenu={handleContextMenu}
    >
      {showUnreadDivider && (
        <div className="section-divider-with-lines">
          <span className="section-divider-with-lines__label">Непрочитанные сообщения</span>
        </div>
      )}
      {isFailed && (
        <div className="directChatView__failedRow">
          <span className="directChatView__failedLabel">Не отправлено</span>
          <button
            type="button"
            className="directChatView__retryBtn"
            onClick={() => onRetry?.(msg.id)}
          >
            Повторить
          </button>
        </div>
      )}
      <MessengerMessageRow
        alignment={isOwn ? 'user' : 'assistant'}
        animateEnter={animateEnter}
        wrapperClassName={isFailed ? 'chatAssistant__messageWrapper_failed' : undefined}
      >
        <div className={outsideLikeWrapClass}>
          {renderDmBubble()}
          {likeOutsideImageOnly ? (
            <div className="chatAssistant__dmOutsideLikeRow">{likeStripNode}</div>
          ) : null}
        </div>
      </MessengerMessageRow>

      <MessageContextMenu
        msg={contextMenuPos ? msg : null}
        anchorPosition={contextMenuPos}
        isOwn={isOwn}
        currentUserId={currentUserId}
        threadVariant={threadVariant}
        relaxDeleteAgeLimit={relaxDeleteAgeLimit}
        selectedText={contextMenuSelectedText}
        messageText={isDeleted ? '' : (msg.body?.trim() ?? '')}
        onClose={() => {
          setContextMenuPos(null);
          setContextMenuSelectedText('');
        }}
        onReply={(m) => {
          setContextMenuPos(null);
          onReply?.(m);
        }}
        onForward={(m) => {
          setContextMenuPos(null);
          onForward?.(m);
        }}
        onDelete={(m) => {
          setContextMenuPos(null);
          onDelete?.(m);
        }}
        onEdit={
          onStartEdit
            ? (m) => {
                setContextMenuPos(null);
                onStartEdit(m);
              }
            : undefined
        }
        onPin={
          onPin
            ? (m) => {
                setContextMenuPos(null);
                onPin(m);
              }
            : undefined
        }
        onUnpin={
          onUnpin
            ? (m) => {
                setContextMenuPos(null);
                onUnpin(m);
              }
            : undefined
        }
        onSelect={
          onToggleSelect
            ? (m) => {
                setContextMenuPos(null);
                onToggleSelect(m);
              }
            : undefined
        }
        onToggleLike={
          onToggleMessageLike && currentUserId
            ? (m) => {
                setContextMenuPos(null);
                onToggleMessageLike(m);
              }
            : undefined
        }
      />
    </div>
  );
}

export const DirectMessageBubble = React.memo(
  DirectMessageBubbleInner,
  (prev, next) =>
    prev.anchorId === next.anchorId &&
    dmBubbleMessageEqualForMemo(prev.msg, next.msg) &&
    prev.isOwn === next.isOwn &&
    prev.showUnreadDivider === next.showUnreadDivider &&
    prev.onRetry === next.onRetry &&
    prev.onReply === next.onReply &&
    prev.onForward === next.onForward &&
    prev.onDelete === next.onDelete &&
    prev.onStartEdit === next.onStartEdit &&
    prev.onPin === next.onPin &&
    prev.onUnpin === next.onUnpin &&
    prev.onToggleSelect === next.onToggleSelect &&
    prev.onToggleMessageLike === next.onToggleMessageLike &&
    prev.selectionMode === next.selectionMode &&
    prev.currentUserId === next.currentUserId &&
    prev.showIncomingAuthorName === next.showIncomingAuthorName &&
    prev.relaxDeleteAgeLimit === next.relaxDeleteAgeLimit &&
    prev.animateEnter === next.animateEnter &&
    prev.threadVariant === next.threadVariant &&
    prev.sidebarSearchHighlightWords === next.sidebarSearchHighlightWords,
);
